import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Field, Heading, Input, Label, Paragraph, ValidationMessage } from '@digdir/designsystemet-react';
import { OrganizationLayout } from '../../../../components/OrganizationLayout';
import { RolesUtils, RoleInfo } from '../../../../utils/RolesUtils';
import { useInviteUserToOrganizationMutation } from '../../../../hooks/mutations';
import { Routes } from '../../../../enums/Routes';
import { RoleChoices } from '../components/RoleChoices';
import { RoleGuide } from '../components/RoleGuide';
import { SafetyRepFields } from '../components/SafetyRepFields';
import { invitePrefill, safetyRepError } from '../safetyRepView';
import { UserRoles } from '../../../../api/queries';
import { ApiErrorUtils } from '../../../../utils/api-error.utils';
import classes from '../Users.module.css';

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

export const InviteUserPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inviteUserMutation = useInviteUserToOrganizationMutation();
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  // «Kom i gang» opens the page with the safety representative chosen, and gets the user back afterwards
  const [params] = useSearchParams();
  const [prefill] = useState(() => invitePrefill(params));
  // The goal with the safety representative step is mapping the working environment, which lives on HR's home
  const done = prefill.fromGettingStarted ? Routes.HRHomeFullPath : Routes.OrganizationUsersOverviewPage;

  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>(prefill.roles);
  const [safetyRep, setSafetyRep] = useState(prefill.safetyRep);
  const [tried, setTried] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const allRoles: RoleInfo[] = RolesUtils.getInvitableRoles();
  const emailError = tried && !EMAIL_PATTERN.test(email.trim());
  const roleError = tried && selectedRoles.length === 0;
  const isSafetyRep = selectedRoles.includes(UserRoles.SAFETY_REP);
  const teamsError = tried && isSafetyRep && safetyRepError(safetyRep) !== null;
  const errors = [
    emailError && { href: '#invite-email', text: t('organizationPage.users.invite.emailSummary') },
    roleError && { href: `#invite-role-${allRoles[0]?.key}`, text: t('organizationPage.users.invite.roleError') },
    teamsError && { href: '#invite-safety-rep-teams', text: t('organizationPage.users.safetyRep.teamsError') },
  ].filter((error): error is { href: string; text: string } => Boolean(error));

  // Fokus på feiloppsummeringen hver gang skjemaet sendes med feil
  useEffect(() => {
    if (attempt > 0) errorSummaryRef.current?.focus();
  }, [attempt]);

  const handleToggleRole = (roleKey: string) => {
    setSelectedRoles((prev) => (prev.includes(roleKey) ? prev.filter((r) => r !== roleKey) : [...prev, roleKey]));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (inviteUserMutation.isPending) return;
    setTried(true);
    const trimmed = email.trim();
    if (
      !EMAIL_PATTERN.test(trimmed) ||
      selectedRoles.length === 0 ||
      (isSafetyRep && safetyRepError(safetyRep) !== null)
    ) {
      setAttempt((count) => count + 1);
      return;
    }
    inviteUserMutation.mutate(
      { email: trimmed, roles: selectedRoles, safetyRep: isSafetyRep ? safetyRep : undefined },
      {
        onSuccess: () => {
          toast.success(t('users.invite.toast.success', { email: trimmed }));
          navigate(done);
        },
        // The API's messages are in English and for developers, so the user gets one in their language
        onError: (error) => {
          toast.error(t(ApiErrorUtils.isForbiddenError(error) ? 'users.invite.toast.noAccess' : 'users.invite.toast.error'));
        },
      },
    );
  };

  return (
    <OrganizationLayout
      breadcrumbs={[{ label: t('organizationPage.tabs.users'), to: Routes.OrganizationUsersOverviewPage }, { label: t('users.invite.pageTitle'), to: '' }]}
    >
      <form noValidate onSubmit={handleSubmit} className={classes.form}>
        {errors.length > 0 && (
          <div ref={errorSummaryRef} tabIndex={-1} className="ds-error-summary" aria-labelledby="invite-errors">
            <Heading id="invite-errors" level={2} data-size="2xs">
              {t('organizationPage.errorSummary')}
            </Heading>
            <ul className="ds-list">
              {errors.map((error) => (
                <li key={error.href}>
                  <a href={error.href} className="ds-link">
                    {error.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <section aria-labelledby="invite-heading" className={classes.card}>
          <div className={classes.cardIntro}>
            <Heading id="invite-heading" level={2} data-size="sm">
              {t('users.invite.pageTitle')}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('organizationPage.users.invite.description')}
            </Paragraph>
          </div>
          <Field className={classes.emailField}>
            <Label htmlFor="invite-email">{t('organizationPage.users.invite.emailLabel')}</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              value={email}
              disabled={inviteUserMutation.isPending}
              aria-invalid={emailError || undefined}
              onChange={(event) => setEmail(event.target.value)}
            />
            {emailError && <ValidationMessage>{t('organizationPage.users.invite.emailError')}</ValidationMessage>}
          </Field>
          <RoleChoices
            idPrefix="invite-role"
            legend={t('users.invite.sections.roles')}
            description={t('organizationPage.users.invite.rolesHint')}
            roles={allRoles}
            selected={selectedRoles}
            disabled={inviteUserMutation.isPending}
            error={roleError ? t('organizationPage.users.invite.roleError') : undefined}
            onToggle={handleToggleRole}
          />
          {isSafetyRep && (
            <SafetyRepFields
              idPrefix="invite-safety-rep"
              value={safetyRep}
              onChange={setSafetyRep}
              disabled={inviteUserMutation.isPending}
              error={teamsError ? t('organizationPage.users.safetyRep.teamsError') : undefined}
            />
          )}
        </section>

        <div className={classes.actions}>
          <Button type="submit" aria-disabled={inviteUserMutation.isPending || undefined}>
            {inviteUserMutation.isPending ? t('users.invite.buttons.sending') : t('users.invite.buttons.send')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate(done)}
            disabled={inviteUserMutation.isPending}
          >
            {t('users.invite.buttons.cancel')}
          </Button>
        </div>

        <RoleGuide roles={allRoles} />
      </form>
    </OrganizationLayout>
  );
};
